import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Card, Button } from "react-bootstrap";
import { PersonBadge, Plus } from "react-bootstrap-icons";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { handleAddPractitionerList } from "features/cliams/config/claimSlice";
import {
  GetAllPractitonerList,
  GetSpecialityPractitonerList,
} from "features/preauthorization/config/preauthorizationApi";
import DoctorEditModal from "./DoctorEditModal";
import DoctorListItem from "./DoctorListItem";

const DoctorInfoCard = () => {
  const dispatch = useDispatch();
  const { viewClaimInfo, practitionerListAdded } = useSelector(
    (state) => state.claim,
  );
  const { allPermissionType } = useSelector((state) => state.role);
  const claimsSetting = allPermissionType?.[0]?.rolemngmt_name?.find(
    (item) =>
      item?.rolemngmttyp_name === "Claim" ||
      item?.rolemngmttyp_name === "Claims",
  );
  const { practitionerList, practitionerRoleList } = useSelector(
    (state) => state.preauthorization,
  );

  const status = viewClaimInfo?.claim_status;
  const isDisabled = [
    "invalid",
    "ready to submit",
    "rejected",
    "cancelled",
    "in process",
    "validation error",
    "invalid",
  ].includes(status?.toLowerCase());

  console.log({ isDisabled });

  const {
    control,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      practitioner: null,
      specialty: null,
      role: null,
    },
  });

  const [showModal, setShowModal] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [currentEditingId, setCurrentEditingId] = useState(null);

  // Track if we've already initialized practitioners from viewClaimInfo
  const hasInitializedRef = useRef(false);
  const viewClaimIdRef = useRef(null);

  useEffect(() => {
    if (practitionerList?.length === 0) {
      dispatch(GetAllPractitonerList());
    }
  }, [dispatch, practitionerList?.length]);

  const processPractitioner = useCallback(
    async (payloadPract) => {
      // Skip if practitioner is already added
      if (
        practitionerListAdded.some(
          (item) =>
            item.practitioner?.value?.practitioner_license ===
            payloadPract.pract_license,
        )
      ) {
        return;
      }

      let matchedPractitioner = practitionerList.find(
        (p) => p.practitioner_license === payloadPract.pract_license,
      );

      const name = payloadPract?.pract_name;
      const parts = name ? name.split(" ") : [];

      if (!matchedPractitioner && payloadPract) {
        matchedPractitioner = {
          practitioner_id: payloadPract.pract_id,
          practitioner_license: payloadPract.pract_license,
          practitioner_title: "Dr",
          practitioner_firstname: payloadPract.pract_finame || parts?.[0] || "",
          practitioner_fathername: "",
          practitioner_familyname:
            payloadPract.pract_faname || parts?.[1] || "",
        };
      }

      if (!matchedPractitioner) {
        return;
      }

      console.log({ matchedPractitioner });

      const practitionerFormData = {
        value: matchedPractitioner,
        label: `(${matchedPractitioner?.practitioner_license || ""}) - ${
          matchedPractitioner.practitioner_title || "Dr"
        } ${matchedPractitioner.practitioner_firstname || ""} ${
          matchedPractitioner.practitioner_fathername
            ? " " + matchedPractitioner.practitioner_fathername || ""
            : ""
        } ${matchedPractitioner.practitioner_familyname || ""}`,
      };

      console.log({ practitionerFormData });

      const matchedRole = practitionerRoleList?.find(
        (r) =>
          r.careroles_code?.toLowerCase() ===
          (payloadPract?.role || "primary").toLowerCase(),
      ) || {
        careroles_code: payloadPract?.role || "primary",
        careroles_name: "Primary provider",
      };

      const practId =
        matchedPractitioner.practitioner_license || payloadPract.pract_license;

      let matchedSpecialty = null;
      let fetchedSpecialties = [];

      if (practId) {
        try {
          const response = await dispatch(
            GetSpecialityPractitonerList({
              practitioner_id: practId,
            }),
          ).unwrap();

          fetchedSpecialties = response?.data?.speciality || [];

          matchedSpecialty =
            fetchedSpecialties.find(
              (s) =>
                String(s.specialities_name) ===
                  String(payloadPract?.pract_spec) ||
                String(s.specialities_code) ===
                  String(payloadPract?.pract_spec_code),
            ) ||
            (fetchedSpecialties.length > 0 && {
              value: fetchedSpecialties[0].specialities_code,
              label: fetchedSpecialties[0].specialities_name,
            });
        } catch (error) {
          console.error("Failed to fetch specialties.");
          fetchedSpecialties = [];
        }
      }

      if (!matchedSpecialty && payloadPract) {
        matchedSpecialty = {
          value: payloadPract.pract_spec_code,
          label: payloadPract.pract_spec,
        };
        fetchedSpecialties = [
          {
            specialities_code: payloadPract.pract_spec_code,
            specialities_name: payloadPract.pract_spec_name,
          },
        ];
      } else if (!matchedSpecialty) {
        fetchedSpecialties = [];
      }

      const practice = matchedSpecialty
        ? {
            value: matchedSpecialty.value || matchedSpecialty.specialities_code,
            label: matchedSpecialty.label || matchedSpecialty.specialities_name,
          }
        : null;

      dispatch(
        handleAddPractitionerList({
          type: "add",
          payload: {
            id: payloadPract.pract_license,
            practitioner: practitionerFormData,
            practice,
            specialties: fetchedSpecialties,
            originalSpecialties: fetchedSpecialties,
            role: {
              careroles_code: matchedRole.careroles_code,
              careroles_name: matchedRole.careroles_name,
            },
          },
        }),
      );
    },
    [dispatch, practitionerList, practitionerRoleList, practitionerListAdded],
  );

  useEffect(() => {
    const initializeData = async () => {
      // Check if we have the necessary data
      if (
        !viewClaimInfo?.payload_json?.pract_id ||
        !practitionerList ||
        !practitionerRoleList
      ) {
        return;
      }

      const currentClaimId = viewClaimInfo?.claim_id;

      // Only initialize once per claim, or when switching to a different claim
      if (
        hasInitializedRef.current &&
        viewClaimIdRef.current === currentClaimId
      ) {
        return;
      }

      // If we're viewing a different claim, reset the initialization flag
      if (viewClaimIdRef.current !== currentClaimId) {
        hasInitializedRef.current = false;
        viewClaimIdRef.current = currentClaimId;
      }

      const payloadPracts = Object.values(viewClaimInfo.payload_json.pract_id);

      // Process practitioners sequentially
      for (const p of payloadPracts) {
        await processPractitioner(p);
      }

      // Mark as initialized
      hasInitializedRef.current = true;
    };

    initializeData();
  }, [viewClaimInfo?.claim_id, practitionerList, practitionerRoleList]);

  // Reset initialization flag when viewClaimInfo becomes null
  useEffect(() => {
    if (!viewClaimInfo) {
      hasInitializedRef.current = false;
      viewClaimIdRef.current = null;
    }
  }, [viewClaimInfo]);

  const handleInputChange = useCallback(
    async (field, selected) => {
      setValue(field, selected, { shouldValidate: false });
      if (field === "practitioner" && selected) {
        try {
          const response = await dispatch(
            GetSpecialityPractitonerList({
              practitioner_id: selected.value.practitioner_license,
            }),
          ).unwrap();
          const fetchedSpecialties = response?.data?.speciality || [];
          console.log({ fetchedSpecialties });
          setSpecialties(fetchedSpecialties);
          setValue("specialty", {
            value: fetchedSpecialties[0].specialities_code,
            label: fetchedSpecialties[0].specialities_name,
          });
        } catch (error) {
          console.error("Error fetching specialties:", error);
          setSpecialties([]);
          setValue("specialty", null);
        }
      } else if (field === "practitioner" && !selected) {
        setSpecialties([]);
        setValue("specialty", null);
      }
    },
    [dispatch, setValue],
  );

  const handleAddClick = useCallback(() => {
    setCurrentEditingId(null);
    reset();
    setSpecialties([]);
    setShowModal(true);
  }, [reset]);

  const handleSave = useCallback(() => {
    const practitioner = watch("practitioner");
    const specialty = watch("specialty");
    const role = watch("role");

    if (!practitioner || !specialty || !role) {
      if (!practitioner)
        setValue("practitioner", null, { shouldValidate: true });
      if (!specialty) setValue("specialty", null, { shouldValidate: true });
      if (!role) setValue("role", null, { shouldValidate: true });
      return;
    }

    if (currentEditingId) {
      dispatch(
        handleAddPractitionerList({
          type: "delete",
          payload: {
            id: currentEditingId,
          },
        }),
      );
    }

    const newId = practitioner.value.practitioner_license;

    dispatch(
      handleAddPractitionerList({
        type: "add",
        payload: {
          id: newId,
          practitioner,
          practice: specialty,
          specialties,
          originalSpecialties: specialties,
          role: {
            careroles_code: role.value,
            careroles_name: role.label,
          },
        },
      }),
    );
    setCurrentEditingId(null);
    setShowModal(false);
  }, [dispatch, watch, setValue, currentEditingId, specialties]);

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setCurrentEditingId(null);
  }, []);

  const handleEdit = useCallback(
    (item) => {
      setCurrentEditingId(item?.practitioner?.value?.practitioner_license);
      setValue("practitioner", item.practitioner);
      setValue("specialty", item.practice);
      setValue("role", {
        value: item.role.careroles_code,
        label: item.role.careroles_name,
      });
      setSpecialties(item.specialties || []);
      setShowModal(true);
    },
    [setValue],
  );

  const handleDelete = useCallback(
    (item) => {
      dispatch(
        handleAddPractitionerList({
          type: "delete",
          payload: {
            id: item?.practitioner?.value?.practitioner_license,
          },
        }),
      );
    },
    [dispatch],
  );

  return (
    <>
      <div>
        <Card.Header className="d-flex badge-subtle-warning justify-content-between align-items-center border-warning border-bottom">
          <strong className="text-warning fw-bold fs-12">
            <PersonBadge className="me-2" />
            Doctor Information
          </strong>
          {isDisabled && Number(claimsSetting?.submit) === 1 && (
            <Plus
              size={12}
              onClick={handleAddClick}
              role="button"
              className="text-success"
            />
          )}
        </Card.Header>

        <Card.Body className="p-2">
          {practitionerListAdded?.map((item, index) => (
            <DoctorListItem
              key={item?.practitioner?.value?.practitioner_license || index}
              item={item}
              index={index}
              isDisabled={isDisabled}
              claimsSetting={claimsSetting}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </Card.Body>
      </div>

      <DoctorEditModal
        showModal={showModal}
        handleCancel={handleCancel}
        handleSave={handleSave}
        control={control}
        errors={errors}
        practitionerList={practitionerList}
        handleInputChange={handleInputChange}
        specialties={specialties}
        practitionerRoleList={practitionerRoleList}
      />
    </>
  );
};

export default DoctorInfoCard;
